<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import type { Calendar, CalendarEvent } from "../Models/Domain";
import type { User } from "@/Modules/Models/Domain/User";
import type { DateTime } from "luxon";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import useCalendarsStore from "../Store";
import useUserStore from "@/Modules/User/Store";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

import { isNativeCalendar } from "../utils";
import { getDateTime } from "@/Components/Extend/XCalendar/utils";

export interface EventViewProps {
  event: CalendarEvent;
  timezone: string;
}

const calendarsStore = useCalendarsStore();
const userStore = useUserStore();
const { t } = useI18n();

const props = defineProps<EventViewProps>();

const calendar = computed((): Calendar => {
  return calendarsStore.calendarsMap.get(props.event.calendarUuid)!;
});

const patient = computed((): User | undefined => {
  if (!isValidString(props.event.patientUuid)) {
    return undefined;
  }

  return calendarsStore.patientsMap.get(props.event.patientUuid)!;
});

const backgroundColor = computed(() => {
  return calendar.value.backgroundColor;
});

const setupDateTime = (seconds: number): DateTime => {
  return getDateTime(seconds).setZone(props.timezone).setLocale(userStore.user!.language);
};

const eventDateString = computed((): string => {
  const startDate = setupDateTime(props.event.startAt);
  const endDate = setupDateTime(props.event.endAt);

  return `${startDate.toFormat(`EEEE, d '${t("calendars.view.event_view.date_union")}' LLLL    -    HH:mm`)} - ${endDate.toFormat("HH:mm")}`;
});

const calendarName = computed((): string => {
  if (
    isValidString(calendar.value.originCalendarUuid) &&
    hasValue(calendarsStore.calendarsMap.get(calendar.value.originCalendarUuid))
  ) {
    const originCalendar = calendarsStore.calendarsMap.get(calendar.value.originCalendarUuid)!;

    return `${originCalendar.name} - ${calendar.value.name}`;
  }

  return calendar.value.name;
});
</script>

<template>
  <div class="event_view" :style="`--event-color: ${backgroundColor}`">
    <div class="event_view__block">
      <div class="event_view__block__icon">
        <div class="dot_icon" />
      </div>

      <div class="event_view__block__content">
        <p>{{ props.event.summary }}</p>
      </div>
    </div>

    <div class="event_view__block" v-if="hasValue(patient)">
      <div class="event_view__block__icon">
        <TcIcon name="User" />
      </div>

      <div class="event_view__block__content">
        <p style="text-transform: capitalize">
          {{ patient.username }}
        </p>
      </div>
    </div>

    <div class="event_view__block" v-if="!isNativeCalendar(calendar)">
      <div class="event_view__block__icon">
        <TcIcon name="Calendar" />
      </div>

      <div class="event_view__block__content">
        <p>{{ calendarName }}</p>
      </div>
    </div>

    <div class="event_view__block">
      <div class="event_view__block__icon">
        <TcIcon name="Clock" />
      </div>

      <div class="event_view__block__content" style="white-space: pre">
        <p>{{ eventDateString }}</p>
      </div>
    </div>

    <div class="event_view__block" v-if="isValidString(props.event.description)">
      <div class="event_view__block__icon">
        <TcIcon name="Chat" />
      </div>

      <div class="event_view__block__content">
        <p>{{ props.event.description }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.event_view {
  &__block {
    @include sp.margin(b, 4);

    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: $space-4;

    &__icon,
    &__content {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    &__content {
      p {
        display: block;
      }

      p::first-letter {
        text-transform: uppercase;
      }
    }
  }
}

.dot_icon {
  @include radius(s);

  height: $space-5;
  width: $space-5;
  background-color: var(--event-color);
}
</style>
